import { z } from "zod";
import {
  FEATURE_LAYOUTS,
  HOLIDAY_EFFECT_TYPES,
  PRODUCT_TYPES,
  SALE_EVENT_TYPES,
  STAFF_PERMISSIONS,
} from "./enums";

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export const emailSchema = z
  .string()
  .trim()
  .min(3, "Email is required")
  .max(254, "Email is too long")
  .email("Please enter a valid email address")
  .transform((v) => v.toLowerCase());

export const usernameSchema = z
  .string()
  .trim()
  .min(3, "Username must be at least 3 characters long")
  .max(20, "Username must be 20 characters or less")
  .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores");

export const urlIdSchema = z
  .string()
  .trim()
  .min(1, "URL id is required")
  .max(80, "URL id is too long")
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and dashes only");

export const hexColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, "Enter a valid hex color, e.g. #5e99ff");

export const safeUrlSchema = z
  .string()
  .trim()
  .max(2048)
  .refine((v) => /^(https?:\/\/|\/)/i.test(v), "Only http(s) URLs or site-relative paths are allowed");

export const discordSnowflakeSchema = z
  .string()
  .trim()
  .regex(/^\d{17,20}$/, "Enter a valid Discord ID");

export const moneySchema = z.coerce.number().min(0, "Must be zero or more").max(1_000_000);

export const percentageSchema = z.coerce.number().min(0).max(100);

/** datetime-local values lack an offset; ISO strings from the API already have one. */
export const dateTimeSchema = z.string().transform((value, ctx) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a valid date and time" });
    return z.NEVER;
  }
  return parsed.toISOString();
});

export const optionalDateTimeSchema = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((value, ctx) => {
    if (value == null || value === "") return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a valid date and time" });
      return z.NEVER;
    }
    return parsed.toISOString();
  });

export const optionalEmailField = z.preprocess((value) => (typeof value === "string" ? value.trim() : value), z.union([z.literal(""), emailSchema]));

function dropBlankRows(keys: string[]) {
  return (value: unknown) => {
    if (!Array.isArray(value)) return value;
    return value.filter((item) => {
      if (!item || typeof item !== "object") return false;
      const record = item as Record<string, unknown>;
      return keys.some((key) => String(record[key] ?? "").trim() !== "");
    });
  };
}

export function passwordSchema(minLength: number) {
  return z
    .string()
    .min(minLength, `Password must be at least ${minLength} characters long`)
    .max(128, "Password is too long");
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required").max(128),
  remember: z.boolean().default(false),
});
export type LoginInput = z.infer<typeof loginSchema>;

export function registerSchema(minPasswordLength: number) {
  return z
    .object({
      username: usernameSchema,
      email: emailSchema,
      password: passwordSchema(minPasswordLength),
      confirmPassword: z.string(),
      acceptTerms: z.literal(true, { errorMap: () => ({ message: "You must accept the terms of service" }) }),
    })
    .refine((d) => d.password === d.confirmPassword, {
      path: ["confirmPassword"],
      message: "Passwords do not match",
    });
}
export type RegisterInput = z.infer<ReturnType<typeof registerSchema>>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export function resetPasswordSchema(minPasswordLength: number) {
  return z
    .object({
      password: passwordSchema(minPasswordLength),
      confirmPassword: z.string(),
    })
    .refine((d) => d.password === d.confirmPassword, {
      path: ["confirmPassword"],
      message: "Passwords do not match",
    });
}
export type ResetPasswordInput = z.infer<ReturnType<typeof resetPasswordSchema>>;

export const resendVerificationSchema = z.object({ email: emailSchema });

export const changeEmailSchema = z.object({
  newEmail: emailSchema,
  currentPassword: z.string().min(1, "Current password is required").max(128),
});
export type ChangeEmailInput = z.infer<typeof changeEmailSchema>;

// ---------------------------------------------------------------------------
// Storefront
// ---------------------------------------------------------------------------

export function reviewSchema(minimumLength: number) {
  return z.object({
    productId: z.string().min(1, "Choose a product"),
    rating: z.coerce.number().int().min(1, "Choose a rating").max(5),
    comment: z
      .string()
      .trim()
      .min(minimumLength, `Your review must be at least ${minimumLength} characters long`)
      .max(2000, "Your review is too long"),
  });
}
export type ReviewInput = z.infer<ReturnType<typeof reviewSchema>>;

export const applyDiscountSchema = z.object({
  code: z.string().trim().min(1, "Enter a discount code").max(64),
});

export const checkoutSchema = z.object({
  provider: z.enum(["paypal", "paypal-standard", "stripe", "coinbase", "nowpayments"]),
  acceptTerms: z.literal(true, { errorMap: () => ({ message: "You must accept the terms of service" }) }),
  discountCode: z.string().trim().max(64).optional(),
});
export type CheckoutInput = z.infer<typeof checkoutSchema>;

// ---------------------------------------------------------------------------
// Staff: products, versions, bundles, discount codes, sales
// ---------------------------------------------------------------------------

export const productInputSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(120),
    urlId: urlIdSchema,
    productType: z.enum(PRODUCT_TYPES),
    price: moneySchema,
    category: z.string().trim().max(80).default(""),
    description: z.string().trim().min(1, "Description is required").max(20_000),
    dependencies: z.string().trim().max(5_000).default(""),
    serviceMessage: z.string().trim().max(5_000).default(""),
    discordRoleIds: z.array(discordSnowflakeSchema).max(25).default([]),
    hideProduct: z.boolean().default(false),
    pauseSelling: z.boolean().default(false),
    serialRequiresFile: z.boolean().default(false),
    serials: z.array(z.string().trim().min(1).max(200)).max(10_000).default([]),
    licenseEnabled: z.boolean().default(false),
    loaderStatus: z.enum(["detected", "updating", "undetected"]).default("undetected"),
    licenseExpireDays: z.coerce.number().int().min(-1).max(3650).default(-1),
    licenseMaxIps: z.coerce.number().int().min(1).max(1000).default(5),
    licenseBound: z.boolean().default(false),
    plans: z
      .array(
        z.object({
          id: z.string().optional(),
          name: z.string().trim().min(1).max(80),
          durationDays: z.coerce.number().int().min(-1).max(3650),
          price: moneySchema,
          position: z.coerce.number().int().min(0).default(0),
          active: z.boolean().default(true),
        }),
      )
      .max(50)
      .default([]),
  })
  .superRefine((d, ctx) => {
    if (d.productType === "digitalFree" && d.price !== 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["price"], message: "Free products must have a price of 0" });
    }
    if (d.productType === "multiPrice") {
      if (d.plans.filter((p) => p.active).length < 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["plans"], message: "Add at least one active plan" });
      }
      for (const [i, p] of d.plans.entries()) {
        if (p.price <= 0 && p.durationDays !== -1) {
          // lifetime free plans allowed; paid durations need price
        }
        if (p.price < 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["plans", i, "price"], message: "Plan price must be >= 0" });
        }
      }
      return;
    }
    if (d.productType !== "digitalFree" && d.productType !== "service" && d.price <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["price"], message: "Paid products need a price above 0" });
    }
  });
export type ProductInput = z.infer<typeof productInputSchema>;

export const productVersionInputSchema = z.object({
  version: z.string().trim().min(1, "Version is required").max(40),
  changelog: z.string().trim().min(1, "Changelog is required").max(20_000),
  notifyDiscord: z.boolean().default(true),
});
export type ProductVersionInput = z.infer<typeof productVersionInputSchema>;

export const productSortSchema = z.object({
  order: z.array(z.string().min(1)).min(1),
});

export const bundleInputSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120),
  description: z.string().trim().min(1, "Description is required").max(5_000),
  discountPercentage: z.coerce.number().min(1, "Minimum discount is 1%").max(95, "Maximum discount is 95%"),
  productIds: z.array(z.string().min(1)).min(2, "A bundle needs at least two products").max(50),
  active: z.boolean().default(true),
});
export type BundleInput = z.infer<typeof bundleInputSchema>;

export const discountCodeInputSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Code must be at least 2 characters")
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, "Use letters, numbers, dashes and underscores only"),
  discountPercentage: z.coerce.number().min(1, "Minimum discount is 1%").max(100),
  maxUses: z.preprocess((value) => (value === "" || value === undefined ? null : value), z.union([z.null(), z.coerce.number().int().min(1)])).default(null),
  expiresAt: optionalDateTimeSchema.default(null),
});
export type DiscountCodeInput = z.infer<typeof discountCodeInputSchema>;

export const saleInputSchema = z
  .object({
    productIds: z.array(z.string().min(1)).min(1, "Select at least one product"),
    discountPercentage: z.coerce.number().min(1).max(99),
    startDate: dateTimeSchema,
    endDate: dateTimeSchema,
  })
  .refine((d) => new Date(d.endDate) > new Date(d.startDate), {
    path: ["endDate"],
    message: "End date must be after the start date",
  });
export type SaleInput = z.infer<typeof saleInputSchema>;

export const saleEventInputSchema = z.object({
  enabled: z.boolean(),
  type: z.enum(SALE_EVENT_TYPES).nullable(),
  message: z.string().trim().max(300).default(""),
});

// ---------------------------------------------------------------------------
// Staff: licenses, team, users
// ---------------------------------------------------------------------------

export const licenseCreateSchema = z.object({
  userId: z.string().trim().min(1, "User is required"),
  productId: z.string().min(1, "Product is required"),
  expireDays: z.coerce.number().int().min(-1).max(3650).default(-1),
  maxIps: z.coerce.number().int().min(1).max(1000).default(5),
  bound: z.boolean().default(false),
  customAddons: z.string().trim().max(500).default(""),
  note: z.string().trim().max(1000).default(""),
});
export type LicenseCreateInput = z.infer<typeof licenseCreateSchema>;

export const licenseUpdateSchema = z.object({
  expireDays: z.coerce.number().int().min(-1).max(3650).optional(),
  maxIps: z.coerce.number().int().min(1).max(1000).optional(),
  bound: z.boolean().optional(),
  customAddons: z.string().trim().max(500).optional(),
  note: z.string().trim().max(1000).optional(),
  resetIps: z.boolean().optional(),
  resetHwid: z.boolean().optional(),
});
export type LicenseUpdateInput = z.infer<typeof licenseUpdateSchema>;

export const staffPermissionsSchema = z.object(
  Object.fromEntries(STAFF_PERMISSIONS.map((p) => [p, z.boolean().default(false)])) as Record<
    (typeof STAFF_PERMISSIONS)[number],
    z.ZodDefault<z.ZodBoolean>
  >,
);

export const teamMemberAddSchema = z.object({
  identifier: z.string().trim().min(1, "Discord ID, username or email is required").max(254),
  permissions: staffPermissionsSchema,
});
export type TeamMemberAddInput = z.infer<typeof teamMemberAddSchema>;

export const grantProductSchema = z.object({ productId: z.string().min(1) });
export const grantSerialSchema = z.object({
  productId: z.string().min(1),
  key: z.string().trim().min(1).max(200),
});
export const transferProductsSchema = z.object({
  targetIdentifier: z.string().trim().min(1, "Target user is required").max(254),
  productIds: z.array(z.string().min(1)).default([]),
  serialIds: z.array(z.string().min(1)).default([]),
});

// ---------------------------------------------------------------------------
// Staff: settings & page customization
// ---------------------------------------------------------------------------

export const featureItemSchema = z.object({
  icon: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(120),
  description: z.string().trim().min(1).max(600),
  layout: z.enum(FEATURE_LAYOUTS).default("normal"),
  order: z.coerce.number().int().min(0).default(0),
});

export const faqItemSchema = z.object({
  question: z.string().trim().min(1).max(300),
  answer: z.string().trim().min(1).max(3000),
});

export const linkTabSchema = z.object({
  name: z.string().trim().min(1).max(60),
  link: safeUrlSchema,
});

/** Lowercase slug used for category URLs; empty input yields "". */
function slugifyCategory(value: string, max = 80): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, max)
    .replace(/-+$/g, "");
}

export const productCategorySchema = z.preprocess((value) => {
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  const name = typeof record.name === "string" ? record.name : "";
  const url = typeof record.url === "string" ? record.url.trim() : "";
  return { name, url: url || slugifyCategory(name) };
}, z.object({
  name: z.string().trim().min(1).max(60),
  url: urlIdSchema,
}));

export const pageCustomizationSchema = z.object({
  websiteFont: z.string().trim().max(60),
  accentColor: hexColorSchema,
  homePageTitle: z.string().trim().max(200),
  homePageSubtitle: z.string().trim().max(600),
  productsPageTitle: z.string().trim().max(200),
  productsPageSubtitle: z.string().trim().max(600),
  tosPageTitle: z.string().trim().max(200),
  tosPageSubtitle: z.string().trim().max(600),
  privacyPolicyPageTitle: z.string().trim().max(200),
  privacyPolicyPageSubtitle: z.string().trim().max(600),
  aboutUsText: z.string().max(50_000),
  aboutUsVisible: z.boolean(),
  displayStats: z.boolean(),
  displayFeatures: z.boolean(),
  displayReviews: z.boolean(),
  displayProductReviews: z.boolean(),
  showProductStats: z.boolean(),
  displayCTABanner: z.boolean(),
  displayFAQ: z.boolean(),
  features: z.preprocess(dropBlankRows(["icon", "title", "description"]), z.array(featureItemSchema).max(12)),
  faqs: z.preprocess(dropBlankRows(["question", "answer"]), z.array(faqItemSchema).max(50)),
  customNavTabs: z.preprocess(dropBlankRows(["name", "link"]), z.array(linkTabSchema).max(10)),
  customFooterTabs: z.preprocess(dropBlankRows(["name", "link"]), z.array(linkTabSchema).max(20)),
  footerDescription: z.string().trim().max(1000),
  holidayEffectsEnabled: z.boolean(),
  holidayEffectsType: z.enum(HOLIDAY_EFFECT_TYPES).nullable(),
  siteBannerText: z.string().trim().max(300),
});
export type PageCustomizationInput = z.infer<typeof pageCustomizationSchema>;

export const storeSettingsSchema = z.object({
  storeName: z.string().trim().min(1).max(80),
  discordInviteLink: z.union([safeUrlSchema, z.literal("")]),
  paymentCurrency: z.string().trim().length(3).transform((v) => v.toUpperCase()),
  currencySymbol: z.string().trim().min(1).max(5),
  salesTax: z.coerce.number().min(0).max(100),
  seoTitle: z.string().trim().max(200),
  seoDescription: z.string().trim().max(500),
  seoTags: z.string().trim().max(500),
  termsOfService: z.string().max(200_000),
  privacyPolicy: z.string().max(200_000),
  productCategories: z.preprocess(dropBlankRows(["name", "url"]), z.array(productCategorySchema).max(50)),
  socialLinks: z.object({
    discord: z.union([safeUrlSchema, z.literal("")]),
    twitter: z.union([safeUrlSchema, z.literal("")]),
    instagram: z.union([safeUrlSchema, z.literal("")]),
    youtube: z.union([safeUrlSchema, z.literal("")]),
    github: z.union([safeUrlSchema, z.literal("")]),
    tiktok: z.union([safeUrlSchema, z.literal("")]),
    linkedin: z.union([safeUrlSchema, z.literal("")]),
    facebook: z.union([safeUrlSchema, z.literal("")]),
  }),
  displaySocialLinks: z.boolean(),
  discordLoggingChannel: z.union([discordSnowflakeSchema, z.literal("")]),
  discordProductUpdatesChannel: z.union([discordSnowflakeSchema, z.literal("")]),
  discordReviewChannel: z.union([discordSnowflakeSchema, z.literal("")]),
  sendReviewsToDiscord: z.boolean(),
  minimumReviewLength: z.coerce.number().int().min(0).max(1000),
  allowReviewDeletion: z.boolean(),
  apiEnabled: z.boolean(),
  antiPiracyEnabled: z.boolean(),
  emailSettings: z.object({
    enabled: z.boolean(),
    fromEmail: optionalEmailField,
    provider: z.enum(["smtp", "sendgrid", "resend"]),
    sendGridToken: z.string().max(500).optional(),
    resendApiKey: z.string().max(500).optional(),
    smtp: z.object({
      host: z.string().trim().max(253),
      port: z.coerce.number().int().min(0).max(65535),
      secure: z.boolean(),
      user: z.string().trim().max(254),
      password: z.string().max(500).optional(),
    }),
  }),
  paymentMethods: z.object({
    paypal: z.object({
      enabled: z.boolean(),
      accountType: z.enum(["business", "personal"]),
      mode: z.enum(["sandbox", "live"]),
      clientId: z.string().trim().max(500).optional(),
      clientSecret: z.string().trim().max(500).optional(),
      personalEmail: optionalEmailField,
    }),
    stripe: z.object({
      enabled: z.boolean(),
      secretKey: z.string().trim().max(500).optional(),
      webhookSecret: z.string().trim().max(500).optional(),
    }),
    coinbase: z.object({
      enabled: z.boolean(),
      apiKey: z.string().trim().max(500).optional(),
      webhookSecret: z.string().trim().max(500).optional(),
    }),
    nowpayments: z.object({
      enabled: z.boolean(),
      apiKey: z.string().trim().max(500).optional(),
      ipnSecret: z.string().trim().max(500).optional(),
    }),
  }),
});
export type StoreSettingsInput = z.infer<typeof storeSettingsSchema>;

export const testEmailSchema = z.object({ to: emailSchema });
